import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {
  getLatestSubmittedReply,
  getPendingComments,
  getSubmittedComments,
  isPendingComment,
} from '../util/thread-comment-accessors'
import styles from './CompactThread.module.css'
import {CompactComment} from '../shared/CompactComment'
import {CompactContainer} from '../shared/CompactContainer'

/**
 * A condensed, always-visible preview of a comment thread shown below the
 * collapsed ForYouItem header. The file name and lines are surfaced in the
 * header; this preview shows the initial comment, a "N more replies" divider
 * for any middle comments, and the latest reply. Clicking the parent item
 * expands to the full thread interface (which shows the full file path).
 */
export function CompactThread({thread}: {thread: Thread}) {
  const comments = getSubmittedComments(thread)
  const initial = comments[0]
  if (!initial) return null

  const latestReply = getLatestSubmittedReply(thread)
  const hiddenReplyCount = Math.max(comments.length - 2, 0)

  const pendingComments = getPendingComments(thread)

  return (
    <CompactContainer>
      <CompactComment author={initial.author} body={initial.bodyHTML} isPending={isPendingComment(initial)} />
      {hiddenReplyCount > 0 && (
        <div className={styles.moreReplies}>
          <span>{`${hiddenReplyCount} more ${hiddenReplyCount === 1 ? 'reply' : 'replies'}`}</span>
        </div>
      )}
      {latestReply && (
        <CompactComment
          author={latestReply.author}
          body={latestReply.bodyHTML}
          isPending={isPendingComment(latestReply)}
          emphasized
        />
      )}
      {pendingComments.map(comment => (
        <CompactComment
          author={comment.author}
          body={comment.bodyHTML}
          isPending={isPendingComment(comment)}
          key={comment.id}
        />
      ))}
    </CompactContainer>
  )
}
